import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

import {
  getNotifications as getNotificationsApi,
  getUnreadCount as getUnreadCountApi,
  markAsRead as markAsReadApi,
  markAllAsRead as markAllAsReadApi,
} from "../../helpers/";

import { NotificationsActionTypes } from "./constants";
import {
  getNotificationsSuccess,
  getNotificationsError,
  getUnreadCount as getUnreadCountAction,
  getUnreadCountSuccess,
  getUnreadCountError,
  markAsReadSuccess,
  markAsReadError,
  markAllAsReadSuccess,
  markAllAsReadError,
} from "./actions";

interface GetNotificationsData {
  payload: {
    skip: number;
    limit: number;
  };
  type: string;
}

interface MarkAsReadData {
  payload: {
    id: string;
  };
  type: string;
}

// Get Notifications
function* getNotifications({
  payload: { skip, limit },
}: GetNotificationsData): SagaIterator {
  try {
    const response = yield call(getNotificationsApi, { skip, limit });
    if (response?.status === 403 || response?.__handled403) {
      yield put(
        getNotificationsError(
          NotificationsActionTypes.GET_NOTIFICATIONS,
          "Forbidden"
        )
      );
      return;
    }
    const notifications = response.data;
    yield put(
      getNotificationsSuccess(
        NotificationsActionTypes.GET_NOTIFICATIONS,
        notifications,
        skip
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data || error.message;
    yield put(
      getNotificationsError(
        NotificationsActionTypes.GET_NOTIFICATIONS,
        errorMessage
      )
    );
  }
}

// Get Unread Count
function* getUnreadCount(): SagaIterator {
  try {
    const response = yield call(getUnreadCountApi);
    if (response?.status === 403 || response?.__handled403) {
      yield put(
        getUnreadCountError(
          NotificationsActionTypes.GET_UNREAD_COUNT,
          "Forbidden"
        )
      );
      return;
    }
    const count = response?.data?.count ?? 0;
    yield put(
      getUnreadCountSuccess(NotificationsActionTypes.GET_UNREAD_COUNT, count)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data || error.message;
    yield put(
      getUnreadCountError(
        NotificationsActionTypes.GET_UNREAD_COUNT,
        errorMessage
      )
    );
  }
}

// Mark as Read
function* markAsRead({ payload: { id } }: MarkAsReadData): SagaIterator {
  try {
    const response = yield call(markAsReadApi, { id });
    if (response?.status === 403 || response?.__handled403) {
      yield put(
        markAsReadError(NotificationsActionTypes.MARK_AS_READ, "Forbidden")
      );
      return;
    }
    yield put(markAsReadSuccess(NotificationsActionTypes.MARK_AS_READ, { id }));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data || error.message;
    yield put(
      markAsReadError(NotificationsActionTypes.MARK_AS_READ, errorMessage)
    );
  }
}

// Mark All as Read
function* markAllAsRead(): SagaIterator {
  try {
    const response = yield call(markAllAsReadApi);
    if (response?.status === 403 || response?.__handled403) {
      yield put(
        markAllAsReadError(NotificationsActionTypes.MARK_ALL_AS_READ, "Forbidden")
      );
      return;
    }
    yield put(
      markAllAsReadSuccess(
        NotificationsActionTypes.MARK_ALL_AS_READ,
        response?.data
      )
    );
    // Keep unread count in sync with server
    yield put(getUnreadCountAction());
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data || error.message;
    yield put(
      markAllAsReadError(
        NotificationsActionTypes.MARK_ALL_AS_READ,
        errorMessage
      )
    );
  }
}

// Watchers
export function* watchGetNotifications() {
  yield takeEvery(NotificationsActionTypes.GET_NOTIFICATIONS, getNotifications);
}

export function* watchGetUnreadCount() {
  yield takeEvery(NotificationsActionTypes.GET_UNREAD_COUNT, getUnreadCount);
}

export function* watchMarkAsRead() {
  yield takeEvery(NotificationsActionTypes.MARK_AS_READ, markAsRead);
}

export function* watchMarkAllAsRead() {
  yield takeEvery(NotificationsActionTypes.MARK_ALL_AS_READ, markAllAsRead);
}

// Root Saga
function* notificationsSaga() {
  yield all([
    fork(watchGetNotifications),
    fork(watchGetUnreadCount),
    fork(watchMarkAsRead),
    fork(watchMarkAllAsRead),
  ]);
}

export default notificationsSaga;
